import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Form } from "react-bootstrap";
import {
  fetchSemesters,
  deleteSemester,
  clearError,
  clearSuccess,
} from "../../redux/slices/semesterSlice";
import { fetchCourses } from "../../redux/slices/courseSlice";
import { fetchUniversities } from "../../redux/slices/universitySlice";
import SemesterModal from "./SemesterModal";
import InfoPill from "../Common/InfoPill";
import ConfirmModal from "../Common/ConfirmModal";
import DynamicBadge from "../Common/DynamicBadge";
import { FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";

const Semesters = () => {
  const dispatch = useDispatch();
  const { semesters = [], isLoading, error, success } = useSelector((state) => state.semesters);
  const { courses = [] } = useSelector((state) => state.courses);

  const [showModal, setShowModal] = useState(false);
  const [editingSemester, setEditingSemester] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [semesterToDelete, setSemesterToDelete] = useState(null);
  const [courseFilter, setCourseFilter] = useState("");

  useEffect(() => {
    dispatch(fetchSemesters());
    dispatch(fetchCourses());
    dispatch(fetchUniversities()); // Optional, if we want university info in courses
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  const handleEdit = (semester) => {
    setEditingSemester(semester);
    setShowModal(true);
  };

  const handleDeleteClick = (semester) => {
    setSemesterToDelete(semester);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (semesterToDelete) {
      dispatch(deleteSemester(semesterToDelete.id));
      setShowConfirmModal(false);
      setSemesterToDelete(null);
    }
  };

  const filteredSemesters = courseFilter
    ? semesters.filter((s) => s.course_id === courseFilter)
    : semesters;

  const header = [
    {
      prop: "semester_name",
      title: "Semester Name",
      isFilterable: true,
      isSortable: true,
      cell: (row) => (
        <span className="fw-bold text-primary">{row.semester_name}</span>
      )
    },
    {
      prop: "course_name",
      title: "Course",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <DynamicBadge text={row.course_name || "Unknown"} />
      ),
    },
    {
      prop: "university_name",
      title: "University",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <span className="text-muted">{row.university_name || "Unknown"}</span>
      ),
    },
    {
      prop: "createddate",
      title: "Created Date",
      isSortable: true,
      isFilterable: true,
      cell: (row) => formatDateToDDMMYYYY(row.createddate) || "-",
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button variant="outline-dark" size="sm" onClick={() => handleEdit(row)}>
            <FaPencilAlt />
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteClick(row)}>
            <FaRegTrashAlt />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="text-center mt-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading semesters...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Semesters
        </Link>
      </div>

      <DatatableWrapper
        body={filteredSemesters}
        headers={header}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 10, options: [5, 10, 20, 50] }
        }}
      >
        <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1" style={{ maxWidth: "250px" }}>
              <Filter placeholder="Search semesters..." className="w-100" />
            </div>
            <div style={{ maxWidth: "200px" }}>
              <Form.Select
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                className="btn-sm"
              >
                <option value="">All Courses</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.course_name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <PaginationOptions />
            <InfoPill
              left="Total Semesters"
              right={filteredSemesters?.length}
            />
          </div>

          <div className="d-flex align-items-center">
            <Button
              variant="dark"
              className="btn-sm"
              onClick={() => {
                setEditingSemester(null);
                setShowModal(true);
              }}
            >
              New Semester
            </Button>
          </div>
        </div>

        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>

      <SemesterModal show={showModal} onHide={() => setShowModal(false)} semester={editingSemester} />

      <ConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Semester"
        message={`Are you sure you want to delete the semester "${semesterToDelete?.semester_name}"?`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={isLoading}
      />
    </div>
  );
};

export default Semesters;
